import { ProductConfirm } from '../model/product';

/**
 * 검수 기록 한 건이 반려인지 판정한다.
 *
 * 판정값(`reviewResult`)이 우선이고, 값이 없는 과거 데이터는 반려 사유 유무로 보조 판별한다 —
 * 반려는 오랫동안 `isConfirm`에 값을 남기지 않아 NULL로 저장돼 왔기 때문에 `isConfirm === false`로는
 * 어떤 행도 매칭되지 않았다.
 */
export function isRejectedConfirm(confirm: ProductConfirm): boolean {
  if (confirm.reviewResult) return confirm.reviewResult === 'REJECTED';
  return !confirm.isConfirm && !!confirm.rejectComment?.trim();
}

/**
 * 현재 유효한 판정이 반려일 때만 그 기록을 돌려준다.
 *
 * "가장 최근 반려"가 아니라 "최신 판정이 반려인가"를 보는 이유는, 반려 후 수정·승인된 상품이
 * 다른 사유로 비활성화됐을 때 이미 해소된 과거 반려 사유가 다시 안내되는 것을 막기 위해서다.
 * 정렬은 `created_at`이 아니라 `id` 기준인데, 같은 초에 두 행이 들어가도 순서가 유일하게 결정되기 때문이다.
 */
export function findActiveRejection(confirms: ProductConfirm[] | undefined): ProductConfirm | undefined {
  const reviewed = filterReviewConfirms(confirms);
  if (!reviewed.length) return undefined;

  const latest = reviewed[0];
  return isRejectedConfirm(latest) ? latest : undefined;
}

/** isRejectedConfirm과 짝 — 판정값이 우선이고, 값이 없는 과거 데이터는 isConfirm으로 보조 판별한다. */
export function isApprovedConfirm(confirm: ProductConfirm): boolean {
  if (confirm.reviewResult) return confirm.reviewResult === 'APPROVED';
  return !!confirm.isConfirm;
}

/** 오픈 게이트가 보는 최신 판정 — 승인·반려를 여기서 같은 기준(REVIEW·ADMIN_DIRECT만, HOST_SELF 제외)으로 가른다. */
function latestOpenGateConfirm(confirms: ProductConfirm[] | undefined): ProductConfirm | undefined {
  return filterOpenGateConfirms(confirms)[0];
}

/**
 * 최신 판정이 승인(또는 운영자 직접 오픈)인지. 둘 다 없으면(기록 자체가 없으면) false다 — 서버
 * 게이트(assertReviewApproved)와 같은 기준.
 */
export function hasActiveApproval(confirms: ProductConfirm[] | undefined): boolean {
  const latest = latestOpenGateConfirm(confirms);
  return !!latest && isApprovedConfirm(latest);
}

/**
 * 오픈 게이트가 보는 최신 판정이 반려인지. findActiveRejection과 범위가 다르다 — 거기는 검수
 * 이력(REVIEW)만 보는 반려 안내 모달 전용이라 운영자 직접오픈을 반려 해소로 보지 않는다. 여기는
 * 오픈 가능 여부를 가르는 용도라 운영자 직접오픈(ADMIN_DIRECT)으로 반려가 해소된 것도 반영해야
 * 하므로 hasActiveApproval과 같은 소스를 본다 — 서버 게이트(assertReviewApproved)와 같은 기준.
 */
export function isOpenGateRejected(confirms: ProductConfirm[] | undefined): boolean {
  const latest = latestOpenGateConfirm(confirms);
  return !!latest && isRejectedConfirm(latest);
}

/**
 * 검수 절차를 거친 기록만 남기고 최신순으로 돌려준다. "승인/반려 내역" 모달 전용 — 운영자가 목록
 * 토글로 상태만 바꾼 기록(`ADMIN_DIRECT`)이나 호스트가 직접 연 기록(`HOST_SELF`)은 검수 이력이
 * 아니다. 이것들은 `is_confirm`이 1로 저장되므로 걸러내지 않으면 호스트에게 "승인"으로 표시되어,
 * 검수를 요청한 적도 없는 승인 이력이 토글을 켤 때마다 쌓인다.
 *
 * `source`가 비어 있는 행은 이 컬럼이 생기기 이전 데이터인데, 당시에는 검수 API로만 기록이 만들어졌으므로
 * 검수 이력으로 본다.
 */
export function filterReviewConfirms(confirms: ProductConfirm[] | undefined): ProductConfirm[] {
  if (!confirms?.length) return [];

  return confirms.filter((confirm) => !confirm.source || confirm.source === 'REVIEW').sort((a, b) => b.id - a.id);
}

/**
 * 오픈 가능 여부 판단(승인/반려 게이트) 전용 필터. REVIEW·ADMIN_DIRECT는 승인으로 치고(운영자가
 * 사정을 보고 연 것), HOST_SELF는 치지 않는다 — 호스트 본인이 검수 없이 연 기록이라 이 게이트가
 * 막아야 하는 바로 그 경로이기 때문이다. 서버 게이트(assertReviewApproved)와 같은 기준.
 */
export function filterOpenGateConfirms(confirms: ProductConfirm[] | undefined): ProductConfirm[] {
  if (!confirms?.length) return [];

  return confirms.filter((confirm) => confirm.source !== 'HOST_SELF').sort((a, b) => b.id - a.id);
}
